import {
  Box,
  TableRow,
  Paper,
  TableContainer,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableBody,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { JsonViewer } from "../../../components/JsonViewer";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import {
  useGetNotificationCampaignDetails,
  useGetNotificationRecipientDetails,
} from "../../../clients/graphql/pages/notification-queries";
import moment from "moment";

interface NotificationRecipient {
  notificationKey: number;
  userKey: number;
  firstName: string;
  lastName: string;
  email: string;
  deliveryStatus: string;
  ackedAt: string;
  sentAt: string;
  createdAt: string;
}

interface NotificationDeliveryLog {
  notificationDeliveryLogKey: number;
  responsePayload: string;
  attemptNumber: number;
  createdAt: string;
}

export const NotificationCampaignDetailsPage: React.FC = () => {
  const { campaignKey } = useParams<{ campaignKey: string }>();
  const navigate = useNavigate();
  const notificationKey = parseInt(campaignKey!);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState<number | null>(
    null
  );
  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);

  const { loading, error, data } =
    useGetNotificationCampaignDetails(notificationKey);
  const {
    loading: recipientLoading,
    error: recipientError,
    data: recipientData,
  } = useGetNotificationRecipientDetails(selectedRecipient!, {
    skip: !selectedRecipient,
  });

  const getStatusChip = (status: string) => {
    const statusColors: {
      [key: string]: "default" | "success" | "warning" | "error" | "info";
    } = {
      SENT: "success",
      PENDING: "warning",
      FAILED: "error",
      DELIVERED: "success",
      ACKNOWLEDGED: "info",
    };

    return (
      <Chip
        label={status}
        color={statusColors[status] || "default"}
        size="small"
      />
    );
  };

  const filteredRecipients =
    data?.getNotificationCampaignDetails?.recipients?.filter(
      (recipient: NotificationRecipient) => {
        if (!searchTerm) return true;

        const search = searchTerm.toLowerCase();
        return (
          recipient.userKey.toString().includes(search) ||
          recipient.firstName?.toLowerCase().includes(search) ||
          recipient.lastName?.toLowerCase().includes(search) ||
          recipient.email?.toLowerCase().includes(search)
        );
      }
    ) || [];

  const handleRecipientClick = (recipientKey: number) => {
    setSelectedRecipient(recipientKey);
    setRecipientDialogOpen(true);
  };

  const handleCloseRecipientDialog = () => {
    setRecipientDialogOpen(false);
    setSelectedRecipient(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading campaign details: {error.message}
        </Typography>
      </Box>
    );
  }

  const campaign = data?.getNotificationCampaignDetails?.notification;

  if (!campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Campaign not found</Typography>
      </Box>
    );
  }

  const sentPercentage =
    campaign.totalRecipients > 0
      ? Math.round((campaign.sentCount / campaign.totalRecipients) * 100)
      : 0;
  const ackPercentage =
    campaign.totalRecipients > 0
      ? Math.round((campaign.ackedCount / campaign.totalRecipients) * 100)
      : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs>
          <Link component={RouterLink} to="/developer" color="inherit">
            Developer
          </Link>
          <Link
            component={RouterLink}
            to="/developer/notifications"
            color="inherit"
          >
            Notifications
          </Link>
          {campaign.notificationTemplateKey && (
            <Link
              component={RouterLink}
              to={`/developer/notifications/templates/${campaign.notificationTemplateKey}/campaigns`}
              color="inherit"
            >
              Template {campaign.notificationTemplateKey} Campaigns
            </Link>
          )}
          <Typography color="text.primary">Campaign #{campaignKey}</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2, p: 0, color: "text.secondary" }}
        >
          Back
        </Button>
        <Typography variant="h4">Campaign Details #{campaignKey}</Typography>
      </Box>

      {/* Campaign Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Total Recipients
              </Typography>
              <Typography variant="h3" color="primary">
                {campaign.totalRecipients.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Sent
              </Typography>
              <Typography variant="h3" color="success.main">
                {campaign.sentCount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {sentPercentage}% of total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Acknowledged
              </Typography>
              <Typography variant="h3" color="info.main">
                {campaign.ackedCount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ackPercentage}% of total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Target Mode
              </Typography>
              <Chip label={campaign.targetMode} variant="outlined" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Scheduled:{" "}
                {moment(campaign.scheduledAt).format("M/D/YYYY h:mm a")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaign Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Campaign Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Subject
              </Typography>
              <Typography variant="body1">{campaign.subject}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Target Definition
              </Typography>
              <JsonViewer 
                value={campaign.targetDefinition}
                height="300px"
                maxLines={20}
                showExpandButton={true}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Message Body
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {campaign.body}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Recipients Section */}
      <Typography variant="h5" gutterBottom>
        Recipients ({filteredRecipients.length})
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by User Key, Name, or Email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 400 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User Key</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sent At</TableCell>
              <TableCell>Acknowledged At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecipients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {searchTerm
                    ? "No recipients match your search"
                    : "No recipients found"}
                </TableCell>
              </TableRow>
            ) : (
              filteredRecipients.map((recipient: NotificationRecipient) => (
                <TableRow
                  key={recipient.notificationKey}
                  sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                >
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/users/${recipient.userKey}`}
                      style={{ textDecoration: "none" }}
                    >
                      #{recipient.userKey}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {recipient.firstName} {recipient.lastName}
                  </TableCell>
                  <TableCell>{recipient.email}</TableCell>
                  <TableCell>
                    {getStatusChip(recipient.deliveryStatus)}
                  </TableCell>
                  <TableCell>
                    {recipient.sentAt
                      ? moment(recipient.sentAt).format("M/D/YYYY h:mm a")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {recipient.ackedAt
                      ? moment(recipient.ackedAt).format("M/D/YYYY h:mm a")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleRecipientClick(recipient.notificationKey)
                      }
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Recipient Details Dialog */}
      <Dialog
        open={recipientDialogOpen}
        onClose={handleCloseRecipientDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Recipient Details</DialogTitle>
        <DialogContent>
          {recipientLoading && <CircularProgress />}
          {recipientError && (
            <Typography color="error">
              Error loading recipient details: {recipientError.message}
            </Typography>
          )}
          {recipientData && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Recipient Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    User Key
                  </Typography>
                  <Typography>
                    #
                    {
                      recipientData.getNotificationRecipientDetails.recipient
                        .userKey
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography>
                    {
                      recipientData.getNotificationRecipientDetails.recipient
                        .firstName
                    }{" "}
                    {
                      recipientData.getNotificationRecipientDetails.recipient
                        .lastName
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>
                    {
                      recipientData.getNotificationRecipientDetails.recipient
                        .email
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  {getStatusChip(
                    recipientData.getNotificationRecipientDetails.recipient
                      .deliveryStatus
                  )}
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Delivery Logs
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Attempt Number</TableCell>
                      <TableCell>Response Payload</TableCell>
                      <TableCell>Created At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipientData.getNotificationRecipientDetails.deliveryLogs?.map(
                      (log: NotificationDeliveryLog) => (
                        <TableRow key={log.notificationDeliveryLogKey}>
                          <TableCell>{log.attemptNumber}</TableCell>
                          <TableCell>
                            {JSON.stringify(log.responsePayload, null, 2)}
                          </TableCell>
                          <TableCell>
                            {moment(log.createdAt).format("M/D/YYYY h:mm a")}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRecipientDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
